/**
 * Vellatini PDP — two-way sticky buy box (owner 2026-09-04: "kéo xuống rồi mà
 * scroll bên phải vẫn bị cắt, phải di chuột vào scroll").
 *
 * Before: .pdp-bb was pinned at 100vh with overflow-y:auto (scrollbar hidden),
 * so a buy box taller than the viewport (1896px measured vs 900–1271px) could
 * only be read by hovering the column and wheel-scrolling inside it.
 *
 * Now, when the column is taller than the viewport it follows the page:
 *   top    — position:sticky pinned under the header (scrolling up reached it)
 *   flow   — position:relative, moving with the page between the two pins
 *   bottom — position:sticky with a negative top so its BOTTOM edge pins to
 *            the viewport bottom (scrolling down reached it)
 * Direction flips go through `flow` so nothing jumps. When the content fits,
 * the class is removed and Horizon's native centred sticky box stays.
 * Desktop only (990px, the .pdp-bb breakpoint in vellatini-tokens.css).
 *
 * Horizon scrolls the PAGE inside `.page-wrapper` (overflow-y:auto), not the
 * window — window.scrollY stays 0 — so everything below measures against the
 * nearest scrollable ancestor (the sticky containing scrollport) and falls back
 * to the window only if none is found.
 */
const MQ = window.matchMedia('(min-width: 990px)');
const el = document.querySelector('.pdp-bb.sticky-content--desktop');

if (el) {
  const scroller = (() => {
    for (let n = el.parentElement; n; n = n.parentElement) {
      const cs = getComputedStyle(n);
      if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
    }
    return null;
  })();
  const scrollTarget = scroller || window;
  const scrollPos = () => (scroller ? scroller.scrollTop : window.scrollY);
  const viewTop = () => (scroller ? scroller.getBoundingClientRect().top : 0);
  const viewHeight = () => (scroller ? scroller.clientHeight : window.innerHeight);

  let state = 'top';
  let lastY = scrollPos();
  let tall = false;
  let raf = 0;

  const headerOffset = () => parseFloat(getComputedStyle(el).getPropertyValue('--sticky-header-offset')) || 0;
  const contentHeight = () => el.scrollHeight;

  function setState(next) {
    state = next;
    el.dataset.stick = next;
    if (next === 'top') {
      el.style.setProperty('--vt-stick-top', `${headerOffset()}px`);
      el.style.removeProperty('--vt-flow-y');
    } else if (next === 'bottom') {
      el.style.setProperty('--vt-stick-top', `${viewHeight() - contentHeight()}px`);
      el.style.removeProperty('--vt-flow-y');
    }
  }

  // Freeze the column where it currently sits, in flow: rendered = natural + y.
  function toFlow() {
    const rendered = el.getBoundingClientRect().top;
    el.dataset.stick = 'flow';
    el.style.setProperty('--vt-flow-y', '0px');
    const natural = el.getBoundingClientRect().top; // forces one layout, direction flips only
    el.style.setProperty('--vt-flow-y', `${rendered - natural}px`);
    state = 'flow';
  }

  function reset() {
    tall = false;
    el.classList.remove('pdp-bb--tall');
    delete el.dataset.stick;
    el.style.removeProperty('--vt-stick-top');
    el.style.removeProperty('--vt-flow-y');
    state = 'top';
  }

  function measure() {
    if (!MQ.matches) return reset();
    const fits = contentHeight() + headerOffset() <= viewHeight();
    if (fits) return reset();
    if (!tall) {
      tall = true;
      el.classList.add('pdp-bb--tall');
      setState('top');
    } else if (state === 'bottom') {
      setState('bottom'); // height changed (accordion, pair mode) — re-pin the bottom edge
    }
  }

  function onScroll() {
    raf = 0;
    const y = scrollPos();
    const dir = y > lastY ? 'down' : y < lastY ? 'up' : null;
    lastY = y;
    if (!dir || !tall) return;
    const r = el.getBoundingClientRect();
    const vt = viewTop();
    if (dir === 'down') {
      if (state === 'top') toFlow();
      else if (state === 'flow' && r.bottom <= vt + viewHeight() + 0.5) setState('bottom');
    } else if (state === 'bottom') {
      toFlow();
    } else if (state === 'flow' && r.top >= vt + headerOffset() - 0.5) {
      setState('top');
    }
  }

  scrollTarget.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(onScroll); }, { passive: true });
  window.addEventListener('resize', measure);
  MQ.addEventListener('change', measure);
  const ro = new ResizeObserver(measure);
  ro.observe(el.querySelector('.group-block') || el);
  measure();

  // vellatini-sibling-swap.js morphed <main>: .pdp-bb survives (same id) but its
  // content and height are the sibling's — re-observe and re-pin from the top.
  document.addEventListener('vellatini:pdp:swapped', () => {
    ro.disconnect();
    ro.observe(el.querySelector('.group-block') || el);
    reset();
    lastY = scrollPos();
    measure();
  });
}
